import axios from 'axios';

/**
 * Coerce an `unknown` caught from a try/catch into a concrete `Error`.
 *
 * - AxiosError: the server's complaint is extracted from `response.data`
 *   (string body, or `.error` / `.message` / `.msg` field) and used as the message,
 *   so toasts and inline error displays show what the backend actually
 *   complained about — not the generic HTTP status line. The status line is
 *   shown only as a fallback: if nothing extractable is present, the axios
 *   error passes through unchanged (its `.message` is the bare status line).
 * - Other real `Error` instances pass through unchanged.
 * - A raw value (a bare response body re-thrown without its axios wrapper,
 *   a string, `null`, …) is run through the same `.error` / `.message` /
 *   `.msg` / string extraction; `fallback` is used when nothing extractable is found
 *   (e.g. an empty or unrecognised body), otherwise `String(err)`.
 *
 * `fallback` is consulted only on the raw-value path — it has no effect on
 * AxiosError or `Error` inputs, which already carry their own message.
 */
export const toError = (err: unknown, fallback?: string): Error => {
  if (axios.isAxiosError(err)) {
    const serverMsg = extractServerMessage(err.response?.data);
    return serverMsg !== null ? new Error(serverMsg) : err;
  }
  if (err instanceof Error) return err;
  const serverMsg = extractServerMessage(err);
  if (serverMsg !== null) return new Error(serverMsg);
  if (fallback) return new Error(fallback);
  return new Error(String(err));
};

/**
 * Field precedence is `error` → `message` → `msg`, and it is load-bearing:
 * `msg` comes last so a body carrying both keeps whichever the newer
 * convention supplies.
 *
 * `msg` is read at all because nap-backend has two error-body conventions. The
 * shared OpenAPI `Error400/404/500` components define `{ error }` and newer
 * handlers use it, but plenty of older ones — the whole of `routes/config.js`
 * among them — answer with `{ msg }`. Without this branch such a body is
 * unextractable, the axios error passes through, and the user sees
 * "Request failed with status code 400" instead of what the server said.
 */
const extractServerMessage = (data: unknown): string | null => {
  if (typeof data === 'string') return data;
  if (data !== null && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.error === 'string') return obj.error;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.msg === 'string') return obj.msg;
  }
  return null;
};
